// Zoom / fit / 1:1 / DPI calibration / panel + grid + ruler toggles.
import { S, emit, setView, safeSet, field } from './state.js';
import { $, toast } from './dom.js';
import { fieldBounds } from './geometry.js';

/** CSS px per file unit at zoom 1 (1 unit = 0.01 mm, 96 CSS px per inch). */
export const BASE = 96 / 2540;
export const ppu = () => BASE * S.zoom;
const CARD_UNITS = 8560; // 85.6 mm credit card

export function setZoom(z, anchor = null) {
  const next = Math.max(0.05, Math.min(16, z));
  if (anchor) S.zoomAnchor = anchor; // { ux, uy, clientX, clientY } kept under the pointer
  S.zoom = next;
  emit('view');
}
export const zoomIn = () => setZoom(S.zoom * 1.25);
export const zoomOut = () => setZoom(S.zoom / 1.25);

export function fitToView() {
  if (!S.label) return;
  const scroll = $('#canvas-scroll');
  const availW = Math.max(100, scroll.clientWidth - 72);
  const availH = Math.max(100, scroll.clientHeight - 80);
  setZoom(Math.min(availW / (S.label.width * BASE), availH / (S.label.height * BASE)));
}

export function actualSize() {
  if (!S.label) return;
  if (!S.dpiRatio) {
    openDPICalibration();
    return;
  }
  setZoom(S.dpiRatio / BASE);
  toast('Actual print size (calibrated)', 'good');
}

export function scrollToField(key) {
  const f = field(key);
  if (!f) return;
  const b = fieldBounds(f);
  const scroll = $('#canvas-scroll');
  const surface = $('#label-surface').getBoundingClientRect();
  const view = scroll.getBoundingClientRect();
  const k = ppu();
  const left = surface.left + b.x * k;
  const top = surface.top + b.y * k;
  const right = left + b.w * k;
  const bottom = top + b.h * k;
  // Only scroll when the field is not already fully in view.
  if (left >= view.left && top >= view.top && right <= view.right && bottom <= view.bottom) return;
  scroll.scrollBy({ left: left - view.left - view.width / 3, top: top - view.top - view.height / 3, behavior: 'smooth' });
}

// ----- DPI calibration ---------------------------------------------------------
let pendingRatio = null;
function setDpiBar(px) {
  const wrap = $('#dpi-bar-wrap');
  px = Math.max(60, Math.min(wrap.clientWidth - 12, px));
  $('#dpi-bar-fill').style.width = `${px}px`;
  $('#dpi-bar-handle').style.left = `${px}px`;
  $('#dpi-bar-card').style.width = `${px}px`;
  $('#dpi-bar-handle').setAttribute('aria-valuenow', String(Math.round(px)));
  pendingRatio = px / CARD_UNITS;
  $('#dpi-readout').textContent = `${px.toFixed(1)} CSS px = 85.6 mm · ~${(pendingRatio * 2540).toFixed(0)} px/inch`;
}
export function openDPICalibration() {
  $('#dpi-overlay').classList.add('visible');
  const wrap = $('#dpi-bar-wrap');
  setDpiBar(Math.min(CARD_UNITS * (S.dpiRatio || BASE), wrap.clientWidth - 20));
  setTimeout(() => $('#dpi-bar-handle').focus(), 30);
}
const closeDpi = () => $('#dpi-overlay').classList.remove('visible');

function wireDpi() {
  const wrap = $('#dpi-bar-wrap');
  const handle = $('#dpi-bar-handle');
  const drag = (e) => {
    e.preventDefault();
    const rect = wrap.getBoundingClientRect();
    const move = (ev) => setDpiBar(ev.clientX - rect.left);
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    move(e);
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };
  wrap.addEventListener('pointerdown', drag);
  handle.addEventListener('keydown', (e) => {
    const cur = parseFloat(handle.style.left) || 200;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') setDpiBar(cur + (e.shiftKey ? 10 : 1));
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') setDpiBar(cur - (e.shiftKey ? 10 : 1));
    else return;
    e.preventDefault();
  });
  $('#dpi-save').onclick = () => {
    if (!pendingRatio) return;
    S.dpiRatio = pendingRatio;
    safeSet('labels.dpiRatio', pendingRatio);
    closeDpi();
    toast('Calibration saved', 'good');
    actualSize();
  };
  $('#dpi-cancel').onclick = closeDpi;
  $('#dpi-reset').onclick = () => {
    S.dpiRatio = null;
    safeSet('labels.dpiRatio', null);
    closeDpi();
    toast('Calibration cleared', 'good');
  };
  $('#dpi-overlay').addEventListener('mousedown', (e) => {
    if (e.target.id === 'dpi-overlay') closeDpi();
  });
}

export const toggleGrid = () => setView({ showGrid: !S.showGrid });
export const toggleRulers = () => setView({ showRulers: !S.showRulers });
export const toggleLeft = () => setView({ leftCollapsed: !S.leftCollapsed });
export const toggleRight = () => setView({ rightCollapsed: !S.rightCollapsed });

export function renderViewChrome() {
  $('#zoom-readout').textContent = `${Math.round(S.zoom * 100)}%`;
  const ws = $('#workspace');
  ws.classList.toggle('left-collapsed', S.leftCollapsed);
  ws.classList.toggle('right-collapsed', S.rightCollapsed);
  for (const [id, on] of [['#btn-grid', S.showGrid], ['#btn-ruler', S.showRulers]]) {
    $(id).classList.toggle('on', on);
    $(id).setAttribute('aria-pressed', String(on));
  }
  $('#stat-canvas').textContent = S.label ? `${(S.label.width / 100).toFixed(1)} × ${(S.label.height / 100).toFixed(1)} mm` : '—';
}

export function initView() {
  $('#btn-zoom-in').onclick = zoomIn;
  $('#btn-zoom-out').onclick = zoomOut;
  $('#btn-fit').onclick = fitToView;
  // Phones / narrow windows: start with the fields rail folded so the label is visible.
  if (window.matchMedia && window.matchMedia('(max-width: 900px)').matches) S.leftCollapsed = true;
  $('#btn-actual').onclick = actualSize;
  $('#btn-grid').onclick = toggleGrid;
  $('#btn-ruler').onclick = toggleRulers;
  $('#btn-toggle-left').onclick = toggleLeft;
  $('#btn-expand-left').onclick = toggleLeft;
  $('#btn-toggle-right').onclick = toggleRight;
  $('#btn-expand-right').onclick = toggleRight;
  wireDpi();
  window.addEventListener('resize', () => emit('view'));
}
